// =============================================================================
// AGC: [AUDIT] LotAuditLogViewer - Full audit log viewer modal/screen
// AGC: Shows complete computation traces, event timeline, and export options
// =============================================================================

import React, { useState, useEffect } from 'react'
import { 
  X, 
  FileText, 
  Download, 
  Clock, 
  ChevronDown, 
  ChevronUp, 
  Loader2, 
  AlertCircle,
  MapPin,
  Thermometer,
  Droplets,
  Sun,
  Target,
  CheckCircle2,
  Leaf,
  Calculator
} from 'lucide-react'
import { GlassCard, Button } from '../shared'
import { getLotAuditLog, downloadAuditLog } from '../../services/audit'
import { useAuth } from '../../hooks/useAuth'
import { useTranslation } from '../../hooks/useTranslation'
import type { 
  LotAuditLog, 
  LotComputationTrace, 
  LotEventLogEntry,
  ComputationStep,
  AuditExportFormat 
} from '../../types'

interface LotAuditLogViewerProps {
  lotId: string
  lotName: string
  fieldId: string
  isOpen: boolean
  onClose: () => void
}

/**
 * AGC: [AUDIT] Format date for display
 */
const formatDate = (isoString: string | undefined): string => {
  if (!isoString) return 'N/A'
  try {
    return new Date(isoString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return 'N/A'
  }
}

/**
 * AGC: [AUDIT] Helper to safely render a value that might be an object
 */
const renderValue = (value: unknown): string => {
  if (value === null || value === undefined) return 'N/A'
  if (typeof value === 'string') return value
  if (typeof value === 'number') return String(value)
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'object') {
    // AGC: [FIX] Handle objects that might be crop recommendations or other nested data
    if ('cropName' in value) return String((value as Record<string, unknown>).cropName)
    if ('name' in value) return String((value as Record<string, unknown>).name)
    if ('value' in value) return renderValue((value as Record<string, unknown>).value)
    // AGC: Fallback to JSON for debugging
    try {
      return JSON.stringify(value, null, 2).substring(0, 100) + '...'
    } catch {
      return '[Object]'
    }
  }
  return String(value)
}

/**
 * AGC: [AUDIT] Computation Step Component
 */
const ComputationStepCard: React.FC<{ step: ComputationStep; index: number }> = ({ step, index }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const { t } = useTranslation()
  
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-3 bg-gray-50 dark:bg-gray-800 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center text-xs font-bold text-blue-700 dark:text-blue-300">
            {index + 1}
          </div>
          <div className="text-left">
            <p className="font-medium text-gray-900 dark:text-white">{step.description}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{step.stepKey}</p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </button>
      
      {isExpanded && (
        <div className="p-3 space-y-3 dark:bg-gray-900">
          {/* AGC: Inputs */}
          {step.inputs && step.inputs.length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">{t('audit.viewer.inputs')}</h5>
              <div className="bg-gray-50 dark:bg-gray-800 rounded p-2 space-y-1">
                {step.inputs.map((input, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">{renderValue(input.name)}</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {renderValue(input.value)} {input.unit || ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* AGC: Formula */}
          <div>
            <h5 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">{t('audit.viewer.formula')}</h5>
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded p-2 font-mono text-xs text-amber-800 dark:text-amber-200 break-all">
              {renderValue(step.formula)}
            </div>
          </div>
          
          {/* AGC: Output */}
          {step.output && (
            <div>
              <h5 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">{t('audit.viewer.output')}</h5>
              <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded p-2 flex justify-between items-center">
                <span className="text-emerald-700 dark:text-emerald-400">{renderValue(step.output.name)}</span>
                <span className="font-bold text-emerald-800 dark:text-emerald-300">
                  {renderValue(step.output.value)} {step.output.unit || ''}
                </span>
              </div>
            </div>
          )}
          
          {/* AGC: Comments */}
          {step.comments && (
            <p className="text-sm text-gray-600 dark:text-gray-400 italic">{renderValue(step.comments)}</p>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * AGC: [AUDIT] Event Log Entry Component
 */
const EventLogCard: React.FC<{ event: LotEventLogEntry }> = ({ event }) => {
  const { t } = useTranslation()
  
  // AGC: Get icon for event type
  const getEventIcon = () => {
    switch (event.type) {
      case 'LOT_CREATED':
      case 'LOT_UPDATED':
        return <MapPin className="w-4 h-4" />
      case 'TASK_CREATED':
      case 'TASK_UPDATED':
      case 'TASK_DELETED':
        return <CheckCircle2 className="w-4 h-4" />
      case 'TASK_COMPLETED':
        return <CheckCircle2 className="w-4 h-4 text-emerald-500" />
      case 'INPUTS_GENERATED':
        return <Leaf className="w-4 h-4" />
      case 'RECOMMENDATION_GENERATED':
        return <Target className="w-4 h-4" />
      case 'PLAN_GENERATED':
        return <Calculator className="w-4 h-4" />
      default:
        return <FileText className="w-4 h-4" />
    }
  }
  
  // AGC: Get event type label
  const getEventTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      LOT_CREATED: t('audit.events.lotCreated'),
      LOT_UPDATED: t('audit.events.lotUpdated'),
      LOT_ARCHIVED: t('audit.events.lotArchived'),
      LOT_UNARCHIVED: t('audit.events.lotUnarchived'),
      TASK_CREATED: t('audit.events.taskCreated'),
      TASK_UPDATED: t('audit.events.taskUpdated'),
      TASK_DELETED: t('audit.events.taskDeleted'),
      TASK_COMPLETED: t('audit.events.taskCompleted'),
      INPUTS_GENERATED: t('audit.events.inputsGenerated'),
      RECOMMENDATION_GENERATED: t('audit.events.recommendationGenerated'),
      PLAN_GENERATED: t('audit.events.planGenerated'),
    }
    return labels[type] || type
  }
  
  // AGC: Get color for event type
  const getEventColor = () => {
    switch (event.type) {
      case 'LOT_CREATED':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200'
      case 'LOT_ARCHIVED':
        return 'bg-red-100 text-red-700 border-red-200'
      case 'TASK_COMPLETED':
        return 'bg-blue-100 text-blue-700 border-blue-200'
      case 'RECOMMENDATION_GENERATED':
      case 'PLAN_GENERATED':
        return 'bg-purple-100 text-purple-700 border-purple-200'
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200'
    }
  }
  
  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg border ${getEventColor()} dark:bg-opacity-20 dark:border-opacity-30`}>
      <div className="mt-0.5">{getEventIcon()}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium text-sm">{getEventTypeLabel(event.type)}</span>
          <span className="text-xs opacity-75">{formatDate(event.timestamp)}</span>
        </div>
        <p className="text-sm mt-1 opacity-90">{renderValue(event.description)}</p>
      </div>
    </div>
  )
}

/**
 * AGC: [AUDIT] Computation Trace Summary Component
 */
const TraceSummary: React.FC<{ trace: LotComputationTrace }> = ({ trace }) => {
  const { t } = useTranslation()
  const [showSteps, setShowSteps] = useState(false)
  
  return (
    <GlassCard className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" padding="md">
      {/* AGC: Trace Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h4 className="font-semibold text-gray-900 dark:text-white">{t('audit.viewer.computationTrace')}</h4>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {formatDate(trace.timestamp)} • v{trace.version}
          </p>
        </div>
        {trace.results?.recommendedCrop && (
          <div className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-3 py-1 rounded-full text-sm font-medium">
            {renderValue(trace.results.recommendedCrop)}
          </div>
        )}
      </div>
      
      {/* AGC: Context Summary */}
      {trace.context && (
        <div className="grid grid-cols-2 gap-3 mb-4">
          {/* AGC: Location */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <MapPin className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <span className="text-xs font-medium text-blue-700 dark:text-blue-300">{t('audit.viewer.location')}</span>
            </div>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              {trace.context.lot?.geolocation?.lat?.toFixed(4) || 'N/A'}, {trace.context.lot?.geolocation?.lng?.toFixed(4) || 'N/A'}
            </p>
          </div>
          
          {/* AGC: Climate Summary */}
          {trace.context.weather?.summary && (
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Thermometer className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                <span className="text-xs font-medium text-amber-700 dark:text-amber-300">{t('audit.viewer.climate')}</span>
              </div>
              <p className="text-sm text-amber-800 dark:text-amber-200">
                {trace.context.weather.summary.avgTemperature?.toFixed(1) || 'N/A'}°C
              </p>
            </div>
          )}
          
          {/* AGC: Precipitation */}
          {trace.context.weather?.summary && (
            <div className="bg-cyan-50 dark:bg-cyan-900/20 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Droplets className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                <span className="text-xs font-medium text-cyan-700 dark:text-cyan-300">{t('audit.viewer.precipitation')}</span>
              </div>
              <p className="text-sm text-cyan-800 dark:text-cyan-200">
                {trace.context.weather.summary.avgPrecipitation?.toFixed(0) || 'N/A'} mm
              </p>
            </div>
          )}
          
          {/* AGC: Years Analyzed */}
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              <span className="text-xs font-medium text-purple-700 dark:text-purple-300">{t('audit.viewer.yearsAnalyzed')}</span>
            </div>
            <p className="text-sm text-purple-800 dark:text-purple-200">
              {trace.context.weather?.yearsAnalyzed || 5} {t('common.years')}
            </p>
          </div>
        </div>
      )}
      
      {/* AGC: Results Summary */}
      {trace.results?.cropOptions && trace.results.cropOptions.length > 0 && (
        <div className="mb-4">
          <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('audit.viewer.topRecommendations')}</h5>
          <div className="space-y-2">
            {trace.results.cropOptions.slice(0, 3).map((crop, idx) => {
              // AGC: [FIX] Handle both 'name' and 'cropName' formats from different audit versions
              const cropName = crop.name || crop.cropName || crop.cropId || 'Unknown'
              const score = typeof crop.suitabilityScore === 'number' ? crop.suitabilityScore : 0
              const suitability = crop.suitability || (score >= 80 ? 'excelente' : score >= 60 ? 'bom' : 'adequado')
              
              return (
                <div 
                  key={crop.cropId || idx}
                  className={`flex items-center justify-between p-2 rounded ${
                    idx === 0 ? 'bg-emerald-50 dark:bg-emerald-900/30' : 'bg-gray-50 dark:bg-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-white">{cropName}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      suitability === 'excelente' 
                        ? 'bg-emerald-200 dark:bg-emerald-800 text-emerald-800 dark:text-emerald-200'
                        : suitability === 'bom'
                          ? 'bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200'
                          : 'bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200'
                    }`}>
                      {suitability}
                    </span>
                  </div>
                  <span className="font-bold text-gray-900 dark:text-white">{score.toFixed(1)}</span>
                </div>
              )
            })}
          </div>
        </div>
      )}
      
      {/* AGC: Show/Hide Steps Button */}
      <button
        onClick={() => setShowSteps(!showSteps)}
        className="w-full flex items-center justify-center gap-2 py-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
      >
        {showSteps ? (
          <>
            <ChevronUp className="w-4 h-4" />
            {t('audit.viewer.hideSteps')}
          </>
        ) : (
          <>
            <ChevronDown className="w-4 h-4" />
            {t('audit.viewer.showSteps')} ({trace.steps?.length || 0})
          </>
        )}
      </button>
      
      {/* AGC: Computation Steps */}
      {showSteps && trace.steps && (
        <div className="mt-4 space-y-2">
          {trace.steps.map((step, idx) => (
            <ComputationStepCard key={step.stepKey} step={step} index={idx} />
          ))}
        </div>
      )}
    </GlassCard>
  )
}

/**
 * AGC: [AUDIT] Main Audit Log Viewer Component
 */
export const LotAuditLogViewer: React.FC<LotAuditLogViewerProps> = ({
  lotId,
  lotName,
  fieldId,
  isOpen,
  onClose,
}) => {
  // AGC: [DEBUG] Log props on every render
  console.log('🔍 AGC: [AUDIT_VIEWER] Render - isOpen:', isOpen, 'lotId:', lotId, 'fieldId:', fieldId, 'lotName:', lotName)
  
  const { t } = useTranslation()
  const { getAuthToken, user } = useAuth()
  
  // AGC: State
  const [isLoading, setIsLoading] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [auditLog, setAuditLog] = useState<LotAuditLog | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'traces' | 'events'>('traces')
  
  // AGC: Fetch audit log when opened
  useEffect(() => {
    const fetchAuditLog = async () => {
      if (!isOpen || !lotId || !fieldId) {
        console.log('🔍 AGC: [AUDIT_VIEWER] Skipping fetch - isOpen:', isOpen, 'lotId:', lotId, 'fieldId:', fieldId)
        return
      }
      
      console.log('📋 AGC: [AUDIT_VIEWER] Fetching audit log for lot:', lotId)
      setIsLoading(true)
      setError(null)
      
      try {
        const token = await getAuthToken()
        console.log('📋 AGC: [AUDIT_VIEWER] Calling API...')
        const log = await getLotAuditLog(lotId, fieldId, token, user?.uid, {
          tracesLimit: 10,
          eventsLimit: 100,
        })
        console.log('📋 AGC: [AUDIT_VIEWER] Received log:', log ? `${log.computationTraces?.length || 0} traces, ${log.eventLogs?.length || 0} events` : 'NULL')
        setAuditLog(log)
      } catch (err) {
        console.error('❌ AGC: [AUDIT_VIEWER] Error fetching audit log:', err)
        setError(t('audit.errorLoading'))
      } finally {
        setIsLoading(false)
        console.log('📋 AGC: [AUDIT_VIEWER] Fetch complete, isLoading: false')
      }
    }
    
    fetchAuditLog()
  }, [isOpen, lotId, fieldId, getAuthToken, user?.uid, t])
  
  // AGC: Handle download
  const handleDownload = async (format: AuditExportFormat) => {
    setIsDownloading(true)
    try {
      const token = await getAuthToken()
      await downloadAuditLog(lotId, fieldId, format, lotName, token, user?.uid)
    } catch (err) {
      console.error('❌ AGC: [AUDIT] Download error:', err)
    } finally {
      setIsDownloading(false)
    }
  }
  
  if (!isOpen) {
    console.log('🔍 AGC: [AUDIT_VIEWER] NOT rendering - isOpen is false')
    return null
  }
  
  console.log('✅ AGC: [AUDIT_VIEWER] RENDERING modal overlay!')
  
  return (
    <div className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        {/* AGC: Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900 dark:text-white">{t('audit.viewer.title')}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{lotName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>
        
        {/* AGC: Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('traces')}
            className={`flex-1 py-3 text-sm font-medium ${
              activeTab === 'traces'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {t('audit.viewer.tabTraces')} ({auditLog?.totalTraces || 0})
          </button>
          <button
            onClick={() => setActiveTab('events')}
            className={`flex-1 py-3 text-sm font-medium ${
              activeTab === 'events'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {t('audit.viewer.tabEvents')} ({auditLog?.totalEvents || 0})
          </button>
        </div>
        
        {/* AGC: Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-2" />
              <span className="text-gray-500">{t('common.loading')}</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-amber-600">
              <AlertCircle className="w-8 h-8 mb-2" />
              <span>{error}</span>
            </div>
          ) : auditLog ? (
            <>
              {activeTab === 'traces' ? (
                <div className="space-y-4">
                  {auditLog.computationTraces && auditLog.computationTraces.length > 0 ? (
                    auditLog.computationTraces.map((trace) => (
                      <TraceSummary key={trace.id} trace={trace} />
                    ))
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <Calculator className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>{t('audit.viewer.noTraces')}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {auditLog.eventLogs && auditLog.eventLogs.length > 0 ? (
                    auditLog.eventLogs.map((event) => (
                      <EventLogCard key={event.id} event={event} />
                    ))
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>{t('audit.viewer.noEvents')}</p>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>{t('audit.noData')}</p>
            </div>
          )}
        </div>
        
        {/* AGC: Footer with Download Actions */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500 dark:text-gray-400">{t('audit.viewer.downloadAs')}</span>
            <div className="flex gap-2">
              <Button
                onClick={() => handleDownload('json')}
                variant="secondary"
                size="sm"
                disabled={isDownloading}
              >
                {isDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                JSON
              </Button>
              <Button
                onClick={() => handleDownload('xml')}
                variant="secondary"
                size="sm"
                disabled={isDownloading}
              >
                {isDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                XML
              </Button>
              <Button
                onClick={() => handleDownload('pdf')}
                variant="secondary"
                size="sm"
                disabled={isDownloading}
              >
                {isDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                PDF
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LotAuditLogViewer


